import { Location, Path, useLocation, useNavigate } from "react-router-dom";

/**
 * Navigation order, grouped into sidebar sections. The first tab is also the
 * default for missing/invalid URLs.
 */
export const SETTINGS_DEFINITIONS = [
  { slug: "accounts", label: "Accounts", icon: "account_balance", section: 0 },
  { slug: "categories", label: "Categories", icon: "category", section: 0 },
  { slug: "tags", label: "Tags", icon: "sell", section: 0 },
  { slug: "rules", label: "Rules", icon: "gavel", section: 0 },
  { slug: "preferences", label: "Preferences", icon: "tune", section: 1 },
  { slug: "billing", label: "Billing", icon: "credit_card", section: 1 },
  { slug: "profile", label: "Profile", icon: "person", section: 1 },
  { slug: "support", label: "Support", icon: "help", section: 1 },
  { slug: "agents", label: "Agents", icon: "smart_toy", section: 1 },
  { slug: "dev", label: "Dev", icon: "code", section: 1, devOnly: true },
] as const;

// Adding a definition also requires a renderer in SettingsModal.
export type SettingsTab = (typeof SETTINGS_DEFINITIONS)[number]["slug"];

// Settings is a modal over whichever screen the user is on, so it lives in that
// screen's URL as a query parameter, e.g. /transactions?settings=accounts. The
// screen keeps its own path and other parameters underneath.
const SETTINGS_PARAM = "settings";

// History state on an entry that Settings was opened into from the app, so
// closing knows it can step back to the entry before it.
type SettingsLocationState = { settingsOpenedInApp?: boolean };

/** The Settings tab in the URL, or null if Settings isn't open. */
export function getSettingsTabParam(search: string): string | null {
  return new URLSearchParams(search).get(SETTINGS_PARAM);
}

/** The location with Settings open on `tab`, or closed if `tab` is null. */
export function withSettingsTab(
  location: Pick<Location, "pathname" | "search" | "hash">,
  tab: SettingsTab | null,
): Path {
  const params = new URLSearchParams(location.search);
  if (tab === null) {
    params.delete(SETTINGS_PARAM);
  } else {
    params.set(SETTINGS_PARAM, tab);
  }
  const search = params.toString();
  return {
    pathname: location.pathname,
    search: search === "" ? "" : `?${search}`,
    hash: location.hash,
  };
}

/** Whether this entry was opened into Settings from the entry before it. */
export function wasSettingsOpenedInApp(location: Location): boolean {
  const state = location.state as SettingsLocationState | null;
  return state?.settingsOpenedInApp === true;
}

/** Opens Settings over the current screen, or switches tab if it's open. */
export function useOpenSettings(): (tab: SettingsTab) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return (tab) => {
    if (getSettingsTabParam(location.search) !== null) {
      navigate(withSettingsTab(location, tab), {
        state: location.state,
        replace: true,
      });
      return;
    }
    const state: SettingsLocationState = { settingsOpenedInApp: true };
    navigate(withSettingsTab(location, tab), { state });
  };
}
